/**
 * End Intake Validation Utility
 *
 * Validates that all required fields are filled before ending an intake session.
 * - Screen 1: All qualification questions answered + context asked + notes filled
 * - Screen 2: All P1 (priority 1) questions have values or marked as "asked"
 */

import { getDataCollectionQuestions } from '../dataCollectionQuestions';
import { getQualificationQuestions } from '../questions';
import {
  DataCollectionAnswer,
  DataCollectionQuestion,
  EndIntakeValidationResult,
  MissingField,
  QualificationAnswer,
  QualificationQuestion,
  ScreeningV2Data,
} from '../types';

/**
 * Get the specific incomplete part of a qualification question.
 * Returns null if complete, or an object with the specific missing element.
 */
function getQualificationQuestionIncomplete(
  question: QualificationQuestion,
  answers: Record<string, QualificationAnswer>,
  contextAsked: Record<string, boolean>,
  notes: Record<string, string>
): MissingField | null {
  const answer = answers[question.id];
  const asked = contextAsked[question.id];
  const note = notes[question.id];

  // Check if has answer
  if (answer === undefined) {
    return {
      screen: 'Screen 1 (Qualification)',
      fieldId: question.id,
      label: question.text,
    };
  }

  // noteOptional questions with the expected answer skip note + context entirely
  const expectedAnswerStr = question.expectedAnswer ? 'yes' : 'no';
  const isExpectedAnswer = answer === expectedAnswerStr;
  if (question.noteOptional && isExpectedAnswer) {
    return null;
  }

  // Check if "Why" note is filled (required when answer is given but context not confirmed)
  if (!note || note.trim() === '') {
    return {
      screen: 'Screen 1 (Qualification)',
      fieldId: `${question.id}-note`,
      label: `${question.text} — Why note`,
    };
  }

  // Check if context has been asked
  if (asked !== true) {
    return {
      screen: 'Screen 1 (Qualification)',
      fieldId: `${question.id}-context`,
      label: `${question.text} — Confirm asked`,
    };
  }

  return null;
}

/**
 * Get incomplete fields for a data collection question (P1).
 * Returns array of missing fields with specific sub-question targets.
 */
function getDataCollectionQuestionIncomplete(
  question: DataCollectionQuestion,
  answers: Record<string, DataCollectionAnswer>
): MissingField[] {
  const missingFields: MissingField[] = [];
  const answer = answers[question.id];

  // Group questions - check sub-questions
  if (question.inputType === 'group') {
    if (!question.subQuestions) return [];

    // Check all required sub-questions
    const requiredSubs = question.subQuestions.filter(sq => sq.required);
    requiredSubs.forEach(sub => {
      const subAnswer = answers[sub.id];

      if (sub.inputType === 'ask_client') {
        if (!subAnswer?.asked && (subAnswer?.value === undefined || subAnswer?.value === '')) {
          missingFields.push({
            screen: 'Screen 2 (Data Collection)',
            fieldId: sub.id,
            label: `${question.text} — ${sub.text}`,
          });
        }
      } else if (subAnswer?.value === undefined || subAnswer?.value === '') {
        missingFields.push({
          screen: 'Screen 2 (Data Collection)',
          fieldId: sub.id,
          label: `${question.text} — ${sub.text}`,
        });
      }
    });

    return missingFields;
  }

  // ask_client type - can be marked as "asked" without a value
  if (question.inputType === 'ask_client') {
    if (!answer?.asked && (answer?.value === undefined || answer?.value === '')) {
      missingFields.push({
        screen: 'Screen 2 (Data Collection)',
        fieldId: question.id,
        label: question.text,
      });
    }
    return missingFields;
  }

  // object_array type - needs at least one item in arrayValue OR confirmed as "none"
  if (question.inputType === 'object_array') {
    const hasItems = answer?.arrayValue && answer.arrayValue.length > 0;
    const isNoneConfirmed = answer?.value === 'none';
    if (!hasItems && !isNoneConfirmed) {
      missingFields.push({
        screen: 'Screen 2 (Data Collection)',
        fieldId: question.id,
        label: question.text,
      });
    }
    return missingFields;
  }

  // All other types - need a value
  if (answer?.value === undefined || answer?.value === '') {
    missingFields.push({
      screen: 'Screen 2 (Data Collection)',
      fieldId: question.id,
      label: question.text,
    });
  }

  return missingFields;
}

/**
 * Header field values needed for validation.
 */
export interface HeaderFields {
  phone?: string;
  email?: string;
  lossDate?: string;
  state?: string;
}

/**
 * Validate that all required fields are complete for ending an intake.
 *
 * Validation rules:
 * - Header fields are always required (phone, email, loss date, state)
 * - Screen 1 questions are always required (qualification questions must be answered)
 * - Screen 2 P1 questions are required UNLESS disqualified
 *   (disqualified cases don't need detailed data, but escalated cases still do)
 */
export function validateEndIntake(
  caseType: string,
  v2Data: ScreeningV2Data,
  options?: { isDisqualified?: boolean; isEscalated?: boolean; headerFields?: HeaderFields }
): EndIntakeValidationResult {
  const missingHeaderFields: MissingField[] = [];
  const headerFields = options?.headerFields;

  // Validate header fields (always required)
  if (headerFields) {
    // Phone is required
    if (!headerFields.phone?.trim()) {
      missingHeaderFields.push({
        screen: 'Header',
        fieldId: 'header-phone',
        label: 'Phone',
      });
    }

    // Email is required
    if (!headerFields.email?.trim()) {
      missingHeaderFields.push({
        screen: 'Header',
        fieldId: 'header-email',
        label: 'Email',
      });
    }

    // Loss date is required
    if (!headerFields.lossDate?.trim()) {
      missingHeaderFields.push({
        screen: 'Header',
        fieldId: 'header-lossDate',
        label: 'Loss Date',
      });
    }

    // State is required
    if (!headerFields.state?.trim()) {
      missingHeaderFields.push({
        screen: 'Header',
        fieldId: 'header-state',
        label: 'State',
      });
    }
  }

  // Get questions for this case type
  const qualificationQuestions = getQualificationQuestions(caseType);
  const dataCollectionQuestions = getDataCollectionQuestions(caseType);

  // Screen 1: Always validate all qualification questions with granular field detection
  const missingQualificationFields: MissingField[] = [];
  qualificationQuestions.forEach((question) => {
    const incomplete = getQualificationQuestionIncomplete(
      question,
      v2Data.qualificationAnswers,
      v2Data.qualificationContextAsked,
      v2Data.qualificationNotes
    );
    if (incomplete) {
      missingQualificationFields.push(incomplete);
    }
  });

  // Screen 2: Validate P1 data collection questions (skip only if disqualified)
  // If disqualified, there's no need to collect detailed data
  // Escalated cases still need P1 data collected for attorney review
  const skipP1Validation = options?.isDisqualified;
  const missingDataCollectionFields: MissingField[] = skipP1Validation
    ? []
    : dataCollectionQuestions
        .filter((q) => q.priority === 'P1')
        .flatMap((question) =>
          getDataCollectionQuestionIncomplete(question, v2Data.dataCollectionAnswers)
        );

  const missingFields = [
    ...missingHeaderFields,
    ...missingQualificationFields,
    ...missingDataCollectionFields,
  ];

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Get screen number from screen label.
 * Header fields are on Screen 1 (where the header is displayed).
 */
export function getScreenFromLabel(label: string): 1 | 2 {
  if (label.includes('Header') || label.includes('Screen 1') || label.includes('Qualification')) {
    return 1;
  }
  return 2;
}
